import React, { useState, useEffect } from "react";
import moment from "moment";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getApi, patchApi, deleteApi } from "../../../api/api_instance";
import { API_URLS } from "../../../api/apiUrls";
import {
  extractNotificationCount,
  getNotificationPrefix,
  isNotificationRead,
} from "../../../utils/notificationHelpers";

const NotificationList = ({ onClose, onUpdate }) => {
  const [notificationsData, setNotificationsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [apiUnreadCount, setUnreadCount] = useState(null);
  const navigate = useNavigate();

  const { employeeLogin, organisationLogin } = useSelector(
    (state) => state.auth || {},
  );
  const prefix = getNotificationPrefix({ employeeLogin, organisationLogin });

  const fetchUnreadCount = async () => {
    try {
      const response = await getApi({
        apiUrl: API_URLS.NOTIFICATIONS_UNREAD_COUNT(prefix),
      });
      setUnreadCount(extractNotificationCount(response));
    } catch (error) {
      console.error("Failed to fetch unread notification count:", error);
    }
  };

  const fetchData = async (pageNum = 1, append = false) => {
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    document.body.classList.add("overflow-hidden");
    setIsLoading(true);

    try {
      const url = `${API_URLS.NOTIFICATIONS(prefix)}?page=${pageNum}&limit=10`;
      const response = await getApi({ apiUrl: url });

      let list = [];
      if (response && response.success) {
        list = response.data || [];
      } else if (Array.isArray(response)) {
        list = response;
      } else if (response && response.notifications) {
        list = response.notifications;
      }

      if (append) {
        setNotificationsData((prev) => [...prev, ...list]);
      } else {
        setNotificationsData(list);
        setPage(pageNum);
        setHasMore(true);
      }

      if (list.length < 10) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1, false);
    fetchUnreadCount();

    const intervalId = setInterval(() => {
      fetchData(1, false);
      fetchUnreadCount();
    }, 30000);

    return () => {
      clearInterval(intervalId);
      document.body.classList.remove("overflow-hidden");
      document.body.style.paddingRight = "";
    };
  }, [prefix]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchData(nextPage, true);
  };

  const handleMarkAllRead = async () => {
    try {
      await patchApi({ apiUrl: API_URLS.NOTIFICATIONS_READ_ALL(prefix) });
      setPage(1);
      fetchData(1, false);
      if (typeof onUpdate === "function") {
        onUpdate();
      }
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const handleMarkAsRead = async (notification) => {
    const id = notification._id || notification.id;
    try {
      if (!isNotificationRead(notification)) {
        await patchApi({ apiUrl: API_URLS.NOTIFICATION_READ(prefix, id) });
        setNotificationsData((prev) =>
          prev.map((notif) =>
            notif._id === id || notif.id === id
              ? {
                  ...notif,
                  isRead: true,
                  read: true,
                  is_read: true,
                  seen: true,
                }
              : notif,
          ),
        );
        setUnreadCount((prev) =>
          typeof prev === "number" ? Math.max(prev - 1, 0) : prev,
        );
        if (typeof onUpdate === "function") {
          onUpdate();
        }
      }
    } catch (error) {
      console.error(`Failed to mark notification ${id} as read:`, error);
    }

    const redirection =
      notification.redirectionString ||
      notification.redirectUrl ||
      notification.url;
    if (redirection) {
      navigate(redirection);
      if (typeof onClose === "function") {
        onClose();
      }
    }
  };

  const handleDeleteNotification = async (e, id) => {
    e.stopPropagation();
    try {
      await deleteApi({ apiUrl: API_URLS.NOTIFICATION_DELETE(prefix, id) });
      setNotificationsData((prev) =>
        prev.filter((notif) => notif._id !== id && notif.id !== id),
      );
      fetchUnreadCount();
      if (typeof onUpdate === "function") {
        onUpdate();
      }
    } catch (error) {
      console.error(`Failed to delete notification ${id}:`, error);
    }
  };

  const formatNotificationDate = (createdAt) => {
    if (!createdAt) return "";
    return moment(createdAt).format("MMMM DD, YYYY");
  };

  const formatNotificationTimeOnly = (createdAt) => {
    if (!createdAt) return "";
    return moment(createdAt)
      .format("hh:mm A")
      .replace("AM", "A.M.")
      .replace("PM", "P.M.");
  };

  const localUnreadCount = notificationsData.filter(
    (notif) => !isNotificationRead(notif),
  ).length;
  const displayedUnreadCount = apiUnreadCount ?? localUnreadCount;

  const getGroupedNotifications = () => {
    const todayStr = moment().format("YYYY-MM-DD");
    const yesterdayStr = moment().subtract(1, "days").format("YYYY-MM-DD");

    const groups = {
      Today: [],
      Yesterday: [],
      Older: {},
    };

    notificationsData.forEach((notif) => {
      const dateStr = moment(notif.createdAt).format("YYYY-MM-DD");
      if (dateStr === todayStr) {
        groups.Today.push(notif);
      } else if (dateStr === yesterdayStr) {
        groups.Yesterday.push(notif);
      } else {
        const displayDate = moment(notif.createdAt).format("MMMM DD, YYYY");
        if (!groups.Older[displayDate]) {
          groups.Older[displayDate] = [];
        }
        groups.Older[displayDate].push(notif);
      }
    });

    return groups;
  };

  const grouped = getGroupedNotifications();

  const renderCard = (notification) => {
    const isUnread = !isNotificationRead(notification);

    return (
      <div
        key={notification._id || notification.id}
        className={`relative border rounded-xl p-3.5 mb-3 cursor-pointer transition-all border-l-[5px] border-l-[#06513D]
        ${
          isUnread
            ? "bg-[#F4FBF9] border-[#c8ddd8]"
            : "bg-white border-gray-200"
        }`}
        onClick={() => handleMarkAsRead(notification)}
      >
        <div className="flex justify-between items-stretch gap-4">
          {/* LEFT SIDE: Title & Description */}
          <div className="flex-1 min-w-0 flex flex-col justify-between">
            <div>
              <h3 className="text-[14px] font-bold text-[#06513D] leading-snug">
                {notification.title || "Notification"}
              </h3>
            </div>
            <div className="text-[12.5px] text-gray-700 leading-relaxed mt-2 pr-1">
              {notification.content ||
                notification.description ||
                notification.message}
            </div>
          </div>

          {/* RIGHT SIDE: Split Date/Time & Action Buttons */}
          <div className="shrink-0 w-[140px] flex flex-col justify-between items-end text-right">
            {/* Split Date & Time Display */}
            <div className="text-[11px] text-gray-400 font-medium space-y-0.5">
              <div className="flex items-center justify-end gap-1">
                <svg
                  className="w-3.5 h-3.5 text-gray-400 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>{formatNotificationDate(notification.createdAt)} |</span>
              </div>
              <div className="pr-0.5">
                {formatNotificationTimeOnly(notification.createdAt)}
              </div>
            </div>

            {/* Action Buttons Layer */}
            <div className="flex items-center justify-end gap-3 select-none mt-3">
              {isUnread && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMarkAsRead(notification);
                  }}
                  className="text-[#06513D] hover:text-[#054a36] font-bold text-[12px] whitespace-nowrap transition-colors"
                >
                  Mark as read
                </button>
              )}

              <button
                onClick={(e) =>
                  handleDeleteNotification(
                    e,
                    notification._id || notification.id,
                  )
                }
                className="text-[#FF3B30] hover:text-[#E03B30] transition-colors p-0.5"
                title="Delete notification"
              >
                <svg viewBox="0 0 448 512" className="w-4 h-4 fill-current">
                  <path d="M135.2 17.7L128 32H32C14.3 32 0 46.3 0 64S14.3 96 32 96H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H320l-7.2-14.3C307.4 6.8 296.3 0 284.2 0H163.8c-12.1 0-23.2 6.8-28.6 17.7zM416 128H32L53.2 467c1.6 25.3 22.6 45 47.9 45H346.9c25.3 0 46.3-19.7 47.9-45L416 128z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        .notification-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .notification-scroll::-webkit-scrollbar-track {
          background: #E6EEEC;
          border-radius: 4px;
        }
        .notification-scroll::-webkit-scrollbar-thumb {
          background: #06513D;
          border-radius: 4px;
        }
        .notification-scroll::-webkit-scrollbar-thumb:hover {
          background: #054a36;
        }
      `}</style>

      {/* Transparent overlay layer to capture clicks outside the window to close it, without dark tint or blur */}
      <div
        className="fixed inset-0 z-40 bg-transparent"
        onClick={onClose}
      ></div>

      {/* Notification Main Panel Dropdown */}
      <div
        className="fixed z-50 
        top-14 right-2 w-[calc(100vw-16px)] max-w-[480px]
        sm:top-16 sm:right-4 sm:w-[500px] sm:max-w-[500px]
        md:top-18 md:right-6 md:w-[540px] md:max-w-[540px]
        lg:right-8 lg:w-[560px] lg:max-w-[560px]"
      >
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-in slide-in-from-top-2 duration-300">
          {/* Dropdown Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <h3 className="text-[15px] font-bold text-gray-900">
                Your Notifications
              </h3>
              {displayedUnreadCount > 0 && (
                <span className="bg-[#06513D] text-white text-[11px] font-bold w-5 h-5 flex items-center justify-center rounded-full">
                  {displayedUnreadCount > 9 ? "9+" : displayedUnreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                className="flex items-center gap-1.5 bg-[#06513D] hover:bg-[#054a36] text-white font-bold text-[12px] px-3 py-1.5 rounded-lg transition-colors"
                onClick={handleMarkAllRead}
              >
                ✓ Mark all as read
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Notification List Scroll Engine Container */}
          <div className="notification-scroll max-h-[60vh] overflow-y-auto px-4 py-4">
            {isLoading && notificationsData.length === 0 ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="flex gap-3 items-start border-l-[6px] border-l-gray-200 p-4 border border-gray-100 rounded-2xl bg-white"
                  >
                    <div className="flex-1 space-y-3">
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-1/3"></div>
                      <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : notificationsData.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 17h5l-5 5-5-5h5v-5a7.5 7.5 0 0 1 0-15v5z"
                    />
                  </svg>
                </div>
                <p className="text-sm text-gray-500 font-medium">
                  No notifications yet
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  We'll notify you when something happens
                </p>
              </div>
            ) : (
              <div>
                {/* Today */}
                {grouped.Today.length > 0 && (
                  <div className="mb-3">
                    <h3 className="text-[13px] font-bold text-gray-400 mb-2 tracking-wide">
                      Today
                    </h3>
                    {grouped.Today.map(renderCard)}
                  </div>
                )}

                {/* Yesterday */}
                {grouped.Yesterday.length > 0 && (
                  <div className="mb-4 mt-5">
                    <h3 className="text-[13px] font-bold text-gray-400 mb-2 tracking-wide">
                      Yesterday
                    </h3>
                    {grouped.Yesterday.map(renderCard)}
                  </div>
                )}

                {/* Older Dates */}
                {Object.keys(grouped.Older).map((dateStr) => (
                  <div key={dateStr} className="mb-4 mt-5">
                    <h3 className="text-[13px] font-bold text-gray-400 mb-2 tracking-wide">
                      {dateStr}
                    </h3>
                    {grouped.Older[dateStr].map(renderCard)}
                  </div>
                ))}

                {/* Load More Trigger Button */}
                {hasMore && (
                  <div className="text-center pt-2 pb-1">
                    <button
                      onClick={handleLoadMore}
                      disabled={isLoading}
                      className="px-4 py-2 text-sm text-[#06513D] hover:text-[#054a36] font-bold transition-all disabled:opacity-50"
                    >
                      {isLoading ? "Loading..." : "Load More"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default NotificationList;
